import { memo, useCallback, useMemo } from 'react';
import styled from '@emotion/styled';

import { useCurrentRoute } from '../../../../lib/useCurrentRoute';
import useTracking from '../../../../lib/useTracking';
import DesktopNavItem from './Item';

import type { CSSObject, ThemeProps } from '@emotion/react';
import type { Wws_Page } from '../../../../types/graphql';

export interface NavProps {
  pages?: Partial<Wws_Page>[] | undefined;
}

const NavList = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 0;
  padding: 8px 16px;
`;

const NavRow = styled('ul')`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`;

const DesktopNavWrap = styled('nav')`
  display: none;
  width: 100%;
  ${(props: ThemeProps): CSSObject | undefined =>
    props.theme.components.Nav?.Desktop}

  @media (min-width: 768px) {
    display: block;
    z-index: 1;
  }
`;

interface DesktopNavItemWrapperProps {
  page: Partial<Wws_Page>;
  handleTracking: (selection: string) => void;
}

function DesktopNavItemWrapper({
  page,
  handleTracking,
}: DesktopNavItemWrapperProps) {
  return <DesktopNavItem page={page} handleTracking={handleTracking} />;
}

function DesktopNav({ pages }: NavProps) {
  const currentRoute = useCurrentRoute();

  const userDecisionArea = 'desktop nav';

  const trackGuest = useTracking('guest', currentRoute, userDecisionArea);

  const trackRsvp = useTracking('rsvp', currentRoute, userDecisionArea);

  const handleTracking = useCallback(
    (selection: string): void => {
      if (selection === 'rsvp') {
        trackRsvp(selection, '');
      }

      trackGuest(selection, '');
    },
    [trackGuest, trackRsvp]
  );

  const [pathsFirstRow, pathsSecondRow] = useMemo<
    [Partial<Wws_Page>[], Partial<Wws_Page>[]]
  >(() => {
    if (pages) {
      const splitPoint = Math.ceil(pages.length / 2);

      return [pages.slice(0, splitPoint), pages.slice(splitPoint)];
    }

    return [[], []];
  }, [pages]);

  return (
    <DesktopNavWrap>
      <NavList>
        <NavRow data-testid="desktop-nav-row-1">
          {pathsFirstRow.map((page: Partial<Wws_Page>) => (
            <DesktopNavItemWrapper
              key={page.id}
              page={page}
              handleTracking={handleTracking}
            />
          ))}
        </NavRow>

        <NavRow data-testid="desktop-nav-row-2">
          {pathsSecondRow.map((page: Partial<Wws_Page>) => (
            <DesktopNavItemWrapper
              key={page.id}
              page={page}
              handleTracking={handleTracking}
            />
          ))}
        </NavRow>
      </NavList>
    </DesktopNavWrap>
  );
}

export default memo(DesktopNav);
